import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

// Types pour les quiz
export interface Quiz {
  id: string;
  lesson_id: string;
  title: string;
  description?: string;
  instructions?: string;
  passing_score: number;
  time_limit?: number;
  max_attempts?: number;
  is_randomized: boolean;
  show_correct_answers: boolean;
  show_results_immediately: boolean;
  allow_review: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  tenant_id: number;
  questions?: QuizQuestion[];
}

export interface QuizQuestion {
  id: string;
  quiz_id: string;
  question_text: string;
  question_type: 'multiple_choice' | 'single_choice' | 'true_false' | 'text_input' | 'matching' | 'ordering';
  points: number;
  explanation?: string;
  media_url?: string;
  media_type?: 'image' | 'video' | 'audio';
  sort_order: number;
  is_required: boolean;
  created_at: string;
  updated_at: string;
  tenant_id: number;
  answers?: QuizAnswer[];
}

export interface QuizAnswer {
  id: string;
  question_id: string;
  answer_text: string;
  is_correct: boolean;
  explanation?: string;
  media_url?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
  tenant_id: number;
}

export interface QuizAttempt {
  id: string;
  quiz_id: string;
  student_id: string;
  enrollment_id: string;
  score: number;
  max_score: number;
  percentage: number;
  passed: boolean;
  started_at: string;
  completed_at?: string;
  time_spent: number;
  attempt_number: number;
  status: 'in_progress' | 'completed' | 'abandoned' | 'expired';
  answers_data?: any;
  created_at: string;
  updated_at: string;
  tenant_id: number;
}

export interface StudentAnswer {
  id: string;
  attempt_id: string;
  question_id: string;
  answer_id?: string;
  text_answer?: string;
  selected_answers?: string[];
  is_correct: boolean;
  points_earned: number;
  time_spent: number;
  created_at: string;
  updated_at: string;
  tenant_id: number;
}

// ==================== GESTION DES QUIZ ====================

export const createQuiz = async (quizData: Partial<Quiz>, tenantId: number): Promise<Quiz> => {
  const quizId = uuidv4();
  
  const { data, error } = await supabase
    .from('elearn_quizzes')
    .insert({
      id: quizId,
      ...quizData,
      tenant_id: tenantId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateQuiz = async (id: string, quizData: Partial<Quiz>, tenantId: number): Promise<Quiz> => {
  const { data, error } = await supabase
    .from('elearn_quizzes')
    .update({
      ...quizData,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .eq('tenant_id', tenantId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getQuizByLessonId = async (lessonId: string, tenantId: number): Promise<Quiz | null> => {
  try {
    // Utilisation d'une requête SQL directe pour éviter les problèmes de configuration
    const { data, error } = await supabase.rpc(
      'execute_safe_query',
      {
        query: `
          SELECT q.*, 
                 COALESCE(json_agg(
                   json_build_object(
                     'id', qq.id,
                     'quiz_id', qq.quiz_id,
                     'question_text', qq.question_text,
                     'question_type', qq.question_type,
                     'points', qq.points,
                     'explanation', qq.explanation,
                     'sort_order', qq.sort_order,
                     'is_required', qq.is_required,
                     'created_at', qq.created_at,
                     'updated_at', qq.updated_at,
                     'tenant_id', qq.tenant_id,
                     'answers', COALESCE((
                       SELECT json_agg(
                         json_build_object(
                           'id', qa.id,
                           'question_id', qa.question_id,
                           'answer_text', qa.answer_text,
                           'is_correct', qa.is_correct,
                           'sort_order', qa.sort_order,
                           'created_at', qa.created_at,
                           'updated_at', qa.updated_at,
                           'tenant_id', qa.tenant_id
                         )
                       )
                       FROM elearn_quiz_answers qa
                       WHERE qa.question_id = qq.id AND qa.tenant_id = ${tenantId}
                     ), '[]'::json)
                   )
                 ) FILTER (WHERE qq.id IS NOT NULL), '[]'::json) as questions
          FROM elearn_quizzes q
          LEFT JOIN elearn_quiz_questions qq ON q.id = qq.quiz_id AND qq.tenant_id = ${tenantId}
          WHERE q.lesson_id = '${lessonId}'
          AND q.tenant_id = ${tenantId}
          GROUP BY q.id
        `
      }
    );

    if (error) {
      // Si la fonction RPC n'existe pas ou échoue, essayons une approche plus simple
      console.log('Erreur avec execute_safe_query:', error);
      
      // Récupérer le quiz sans les relations
      const { data: quizData, error: quizError } = await supabase
        .from('elearn_quizzes')
        .select('*')
        .eq('lesson_id', lessonId)
        .eq('tenant_id', tenantId)
        .maybeSingle();
      
      if (quizError) {
        console.error('Erreur lors de la récupération du quiz:', quizError);
        return null;
      }
      
      if (!quizData) return null;
      
      // Récupérer les questions séparément
      const { data: questionsData, error: questionsError } = await supabase
        .from('elearn_quiz_questions')
        .select('*')
        .eq('quiz_id', quizData.id)
        .eq('tenant_id', tenantId);
      
      if (questionsError) {
        console.error('Erreur lors de la récupération des questions:', questionsError);
        return { ...quizData, questions: [] };
      }
      
      // Pour chaque question, récupérer ses réponses
      const questions = await Promise.all(
        (questionsData || []).map(async (question) => {
          const { data: answersData, error: answersError } = await supabase
            .from('elearn_quiz_answers')
            .select('*')
            .eq('question_id', question.id)
            .eq('tenant_id', tenantId);
          
          if (answersError) {
            console.error(`Erreur lors de la récupération des réponses pour la question ${question.id}:`, answersError);
            return { ...question, answers: [] };
          }
          
          return { ...question, answers: answersData || [] };
        })
      );
      
      return { ...quizData, questions };
    }
    
    // Si nous avons des données de la fonction RPC
    if (data && data.length > 0) {
      return data[0];
    }
    
    return null;
  } catch (error) {
    console.error('Erreur dans getQuizByLessonId:', error);
    return null; // Retourner null au lieu de lancer une exception pour éviter les erreurs en cascade
  }
};

export const getQuizById = async (id: string, tenantId: number): Promise<Quiz | null> => {
  try {
    // Utilisation de la fonction RPC execute_safe_query pour éviter les problèmes de configuration
    const { data, error } = await supabase.rpc(
      'execute_safe_query',
      {
        query: `
          SELECT q.*, 
                 COALESCE(json_agg(
                   json_build_object(
                     'id', qq.id,
                     'quiz_id', qq.quiz_id,
                     'question_text', qq.question_text,
                     'question_type', qq.question_type,
                     'points', qq.points,
                     'explanation', qq.explanation,
                     'sort_order', qq.sort_order,
                     'is_required', qq.is_required,
                     'created_at', qq.created_at,
                     'updated_at', qq.updated_at,
                     'tenant_id', qq.tenant_id,
                     'answers', COALESCE((
                       SELECT json_agg(
                         json_build_object(
                           'id', qa.id,
                           'question_id', qa.question_id,
                           'answer_text', qa.answer_text,
                           'is_correct', qa.is_correct,
                           'sort_order', qa.sort_order,
                           'created_at', qa.created_at,
                           'updated_at', qa.updated_at,
                           'tenant_id', qa.tenant_id
                         )
                       )
                       FROM elearn_quiz_answers qa
                       WHERE qa.question_id = qq.id AND qa.tenant_id = ${tenantId}
                     ), '[]'::json)
                   )
                 ) FILTER (WHERE qq.id IS NOT NULL), '[]'::json) as questions
          FROM elearn_quizzes q
          LEFT JOIN elearn_quiz_questions qq ON q.id = qq.quiz_id AND qq.tenant_id = ${tenantId}
          WHERE q.id = '${id}'
          AND q.tenant_id = ${tenantId}
          GROUP BY q.id
        `
      }
    );

    if (error) {
      // Si la fonction RPC échoue, essayons une approche plus simple
      console.log('Erreur avec execute_safe_query dans getQuizById:', error);
      
      // Récupérer le quiz sans les relations
      const { data: quizData, error: quizError } = await supabase
        .from('elearn_quizzes')
        .select('*')
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .maybeSingle();
      
      if (quizError) {
        console.error('Erreur lors de la récupération du quiz par ID:', quizError);
        return null;
      }
      
      if (!quizData) return null;
      
      // Récupérer les questions séparément
      const { data: questionsData, error: questionsError } = await supabase
        .from('elearn_quiz_questions')
        .select('*')
        .eq('quiz_id', quizData.id)
        .eq('tenant_id', tenantId);
      
      if (questionsError) {
        console.error('Erreur lors de la récupération des questions:', questionsError);
        return { ...quizData, questions: [] };
      }
      
      // Pour chaque question, récupérer ses réponses
      const questions = await Promise.all(
        (questionsData || []).map(async (question) => {
          const { data: answersData, error: answersError } = await supabase
            .from('elearn_quiz_answers')
            .select('*')
            .eq('question_id', question.id)
            .eq('tenant_id', tenantId);
          
          if (answersError) {
            console.error(`Erreur lors de la récupération des réponses pour la question ${question.id}:`, answersError);
            return { ...question, answers: [] };
          }
          
          return { ...question, answers: answersData || [] };
        })
      );
      
      return { ...quizData, questions };
    }
    
    // Si nous avons des données de la fonction RPC
    if (data && data.length > 0) {
      return data[0];
    }
    
    return null;
  } catch (error) {
    console.error('Erreur dans getQuizById:', error);
    return null; // Retourner null au lieu de lancer une exception
  }
};

export const deleteQuiz = async (id: string, tenantId: number): Promise<void> => {
  const { error } = await supabase
    .from('elearn_quizzes')
    .delete()
    .eq('id', id)
    .eq('tenant_id', tenantId);

  if (error) throw error;
};

// ==================== GESTION DES QUESTIONS ====================

export const addQuestionToQuiz = async (quizId: string, questionData: Partial<QuizQuestion>, tenantId: number): Promise<QuizQuestion> => {
  const questionId = uuidv4();
  
  // Obtenir le prochain sort_order
  const { data: lastQuestion } = await supabase
    .from('elearn_quiz_questions')
    .select('sort_order')
    .eq('quiz_id', quizId)
    .eq('tenant_id', tenantId)
    .order('sort_order', { ascending: false })
    .limit(1)
    .single();

  const nextSortOrder = lastQuestion ? lastQuestion.sort_order + 1 : 1;

  const { data, error } = await supabase
    .from('elearn_quiz_questions')
    .insert({
      id: questionId,
      quiz_id: quizId,
      ...questionData,
      sort_order: nextSortOrder,
      tenant_id: tenantId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateQuestion = async (id: string, questionData: Partial<QuizQuestion>, tenantId: number): Promise<QuizQuestion> => {
  const { data, error } = await supabase
    .from('elearn_quiz_questions')
    .update({
      ...questionData,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .eq('tenant_id', tenantId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteQuestion = async (id: string, tenantId: number): Promise<void> => {
  const { error } = await supabase
    .from('elearn_quiz_questions')
    .delete()
    .eq('id', id)
    .eq('tenant_id', tenantId);

  if (error) throw error;
};

export const reorderQuestions = async (quizId: string, questionIds: string[], tenantId: number): Promise<void> => {
  const updates = questionIds.map((questionId, index) => ({
    id: questionId,
    sort_order: index + 1,
    updated_at: new Date().toISOString()
  }));

  for (const update of updates) {
    const { error } = await supabase
      .from('elearn_quiz_questions')
      .update({ sort_order: update.sort_order, updated_at: update.updated_at })
      .eq('id', update.id)
      .eq('tenant_id', tenantId);

    if (error) throw error;
  }
};

// ==================== GESTION DES RÉPONSES ====================

export const addAnswerToQuestion = async (questionId: string, answerData: Partial<QuizAnswer>, tenantId: number): Promise<QuizAnswer> => {
  const answerId = uuidv4();
  
  // Obtenir le prochain sort_order
  const { data: lastAnswer } = await supabase
    .from('elearn_quiz_answers')
    .select('sort_order')
    .eq('question_id', questionId)
    .eq('tenant_id', tenantId)
    .order('sort_order', { ascending: false })
    .limit(1)
    .single();

  const nextSortOrder = lastAnswer ? lastAnswer.sort_order + 1 : 1;

  const { data, error } = await supabase
    .from('elearn_quiz_answers')
    .insert({
      id: answerId,
      question_id: questionId,
      ...answerData,
      sort_order: nextSortOrder,
      tenant_id: tenantId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateAnswer = async (id: string, answerData: Partial<QuizAnswer>, tenantId: number): Promise<QuizAnswer> => {
  const { data, error } = await supabase
    .from('elearn_quiz_answers')
    .update({
      ...answerData,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .eq('tenant_id', tenantId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteAnswer = async (id: string, tenantId: number): Promise<void> => {
  const { error } = await supabase
    .from('elearn_quiz_answers')
    .delete()
    .eq('id', id)
    .eq('tenant_id', tenantId);

  if (error) throw error;
};

// ==================== GESTION DES TENTATIVES ====================

export const startQuizAttempt = async (quizId: string, studentId: string, enrollmentId: string, tenantId: number): Promise<QuizAttempt> => {
  try {
    const attemptId = uuidv4();
    
    // Vérifier le nombre de tentatives existantes avec une approche robuste
    let attemptNumber = 1;
    try {
      // Utiliser l'approche standard pour éviter les problèmes avec les fonctions RPC
      const { data: attemptsData, error: attemptsError } = await supabase
        .from('elearn_quiz_attempts')
        .select('attempt_number')
        .eq('quiz_id', quizId)
        .eq('student_id', studentId)
        .eq('tenant_id', tenantId)
        .order('attempt_number', { ascending: false })
        .limit(1);
      
      if (!attemptsError && attemptsData && attemptsData.length > 0) {
        attemptNumber = attemptsData[0].attempt_number + 1;
      }
    } catch (e) {
      console.error('Erreur lors de la vérification des tentatives existantes:', e);
      // Continuer avec la valeur par défaut (1)
    }
    
    // Vérifier les limites de tentatives
    let maxAttempts = null;
    try {
      // Utiliser l'approche standard pour éviter les problèmes avec les fonctions RPC
      const { data: quizData, error: quizError } = await supabase
        .from('elearn_quizzes')
        .select('max_attempts')
        .eq('id', quizId)
        .eq('tenant_id', tenantId)
        .maybeSingle();
      
      if (!quizError && quizData) {
        maxAttempts = quizData.max_attempts;
      }
    } catch (e) {
      console.error('Erreur lors de la vérification des limites de tentatives:', e);
    }
    
    if (maxAttempts && attemptNumber > maxAttempts) {
      throw new Error('Nombre maximum de tentatives atteint');
    }
    
    // Créer une nouvelle tentative
    const now = new Date().toISOString();
    
    // Utiliser l'approche standard pour éviter les problèmes avec les fonctions RPC
    const { data, error } = await supabase
      .from('elearn_quiz_attempts')
      .insert({
        id: attemptId,
        quiz_id: quizId,
        student_id: studentId,
        enrollment_id: enrollmentId,
        attempt_number: attemptNumber,
        status: 'in_progress',
        tenant_id: tenantId,
        started_at: now,
        created_at: now,
        updated_at: now,
        // Initialiser les champs numériques avec des valeurs par défaut pour éviter NaN
        score: 0,
        max_score: 0,
        percentage: 0,
        passed: false,
        time_spent: 0
      })
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('Erreur lors de la création de la tentative:', error);
      throw error;
    }
    
    if (data) {
      console.log('startQuizAttempt - tentative créée avec succès:', data);
      return data;
    }
    
    throw new Error('Impossible de créer une nouvelle tentative de quiz');
  } catch (error) {
    console.error('Erreur dans startQuizAttempt:', error);
    throw error;
  }
};

export const submitStudentAnswer = async (attemptId: string, questionId: string, answerData: Partial<StudentAnswer>, tenantId: number): Promise<StudentAnswer> => {
  try {
    const studentAnswerId = uuidv4();
    const now = new Date().toISOString();
    
    // Vérifier si la réponse est correcte
    let isCorrectAnswer = false;
    let pointsEarned = 0;
    
    // Gérer le cas spécial des questions vrai/faux
    if (answerData.answer_id === 'true' || answerData.answer_id === 'false') {
      console.log('Question vrai/faux détectée avec réponse:', answerData.answer_id);
      
      // Récupérer la question pour vérifier si c'est une question vrai/faux
      const { data: question, error: questionTypeError } = await supabase
        .from('elearn_quiz_questions')
        .select('question_type')
        .eq('id', questionId)
        .eq('tenant_id', tenantId)
        .maybeSingle();
      
      if (questionTypeError) {
        console.error('Erreur lors de la vérification du type de question:', questionTypeError);
      } else if (question?.question_type === 'true_false') {
        // Pour les questions vrai/faux, on traite différemment
        // Récupérer la bonne réponse pour cette question
        const { data: correctAnswer, error: correctAnswerError } = await supabase
          .from('elearn_quiz_answers')
          .select('is_correct, answer_text')
          .eq('question_id', questionId)
          .eq('tenant_id', tenantId);
        
        if (correctAnswerError) {
          console.error('Erreur lors de la récupération de la réponse correcte:', correctAnswerError);
        } else if (correctAnswer && correctAnswer.length > 0) {
          // Vérifier si la réponse de l'utilisateur correspond à la réponse correcte
          const userAnswerIsTrue = answerData.answer_id === 'true';
          const correctAnswerIsTrue = correctAnswer.some(a => 
            a.answer_text.toLowerCase() === 'vrai' || 
            a.answer_text.toLowerCase() === 'true'
          );
          
          isCorrectAnswer = userAnswerIsTrue === correctAnswerIsTrue;
          console.log('Réponse vrai/faux correcte?', isCorrectAnswer);
          
          // Supprimer answer_id pour éviter l'erreur UUID
          delete answerData.answer_id;
          // Stocker la réponse comme text_answer à la place
          answerData.text_answer = userAnswerIsTrue ? 'true' : 'false';
        }
      }
    }
    // Vérifier si c'est une question à choix unique avec un UUID valide
    else if (answerData.answer_id && answerData.answer_id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      console.log('Vérification réponse unique:', answerData.answer_id);
      // Utiliser l'approche standard pour éviter les problèmes avec les fonctions RPC
      const { data: answer, error: answerError } = await supabase
        .from('elearn_quiz_answers')
        .select('is_correct')
        .eq('id', answerData.answer_id)
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (answerError) {
        console.error('Erreur lors de la vérification de la réponse:', answerError);
      } else {
        isCorrectAnswer = answer?.is_correct || false;
        console.log('Réponse correcte?', isCorrectAnswer);
      }
    } else if (answerData.selected_answers && answerData.selected_answers.length > 0) {
      console.log('Vérification réponses multiples:', answerData.selected_answers);
      // Question à choix multiples
      const { data: correctAnswersData, error: correctAnswersError } = await supabase
        .from('elearn_quiz_answers')
        .select('id, is_correct')
        .eq('question_id', questionId)
        .eq('tenant_id', tenantId);
      
      if (correctAnswersError) {
        console.error('Erreur lors de la vérification des réponses multiples:', correctAnswersError);
      } else if (correctAnswersData && correctAnswersData.length > 0) {
        // Vérifier si toutes les réponses sélectionnées sont correctes et si toutes les réponses correctes sont sélectionnées
        const correctAnswerIds = correctAnswersData
          .filter(a => a.is_correct)
          .map(a => a.id);
        
        const selectedCorrectAnswers = answerData.selected_answers.filter(id => 
          correctAnswerIds.includes(id)
        );
        
        const incorrectSelections = answerData.selected_answers.filter(id => 
          !correctAnswerIds.includes(id)
        );
        
        // La réponse est correcte si toutes les réponses correctes sont sélectionnées et aucune réponse incorrecte n'est sélectionnée
        isCorrectAnswer = 
          selectedCorrectAnswers.length === correctAnswerIds.length && 
          incorrectSelections.length === 0;
      }
    } else if (answerData.text_answer) {
      // Question à réponse libre, à implémenter la logique de vérification
      // Pour l'instant, on considère que c'est correct (sera vérifié manuellement)
      isCorrectAnswer = true;
    }
    
    // Récupérer les points de la question
    const { data: questionData, error: questionError } = await supabase
      .from('elearn_quiz_questions')
      .select('points')
      .eq('id', questionId)
      .eq('tenant_id', tenantId)
      .maybeSingle();
    
    if (questionError) {
      console.error('Erreur lors de la récupération des points de la question:', questionError);
    } else if (questionData) {
      pointsEarned = questionData.points || 0;
      console.log('Points gagnés:', pointsEarned);
    }
    
    // Si la réponse est incorrecte, aucun point n'est gagné
    if (!isCorrectAnswer) {
      pointsEarned = 0;
    }
    
    // Préparer les données pour l'insertion
    const insertData: any = {
      id: studentAnswerId,
      attempt_id: attemptId,
      question_id: questionId,
      is_correct: isCorrectAnswer,
      points_earned: pointsEarned,
      tenant_id: tenantId,
      created_at: now,
      updated_at: now
    };
    
    // Ajouter les champs spécifiques selon le type de réponse
    // IMPORTANT: Ne pas ajouter de champs avec des valeurs null ou undefined
    if (answerData.answer_id && typeof answerData.answer_id === 'string') {
      // Vérifier que answer_id est un UUID valide
      if (answerData.answer_id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        insertData.answer_id = answerData.answer_id;
      } else {
        // Si ce n'est pas un UUID valide, ne pas l'inclure
        console.log('answer_id non valide, non inclus dans l\'insertion:', answerData.answer_id);
      }
    }
    
    if (answerData.selected_answers && Array.isArray(answerData.selected_answers)) {
      // S'assurer que selected_answers est un tableau valide
      insertData.selected_answers = answerData.selected_answers;
    }
    
    if (answerData.text_answer && typeof answerData.text_answer === 'string') {
      insertData.text_answer = answerData.text_answer;
    }
    
    // Insérer la réponse de l'étudiant avec l'approche standard
    const { data, error } = await supabase
      .from('elearn_quiz_student_answers')
      .insert(insertData)
      .select()
      .single();
    
    if (error) {
      console.error('Erreur lors de l\'insertion de la réponse:', error);
      throw error;
    }
    
    if (data) {
      console.log('Réponse insérée avec succès');
      return data;
    }
    
    throw new Error('Impossible d\'enregistrer la réponse de l\'étudiant');
  } catch (error) {
    console.error('Erreur dans submitStudentAnswer:', error);
    throw error;
  }
};

export const completeQuizAttempt = async (attemptId: string, tenantId: number): Promise<QuizAttempt> => {
  console.log('completeQuizAttempt appelé avec:', { attemptId, tenantId });
  try {
    // Vérifier d'abord si la tentative existe
    const { data: attemptData, error: attemptError } = await supabase
      .from('elearn_quiz_attempts')
      .select('*, quiz_id')
      .eq('id', attemptId)
      .eq('tenant_id', tenantId)
      .single();
      
    if (attemptError) {
      console.error('Erreur lors de la récupération de la tentative:', attemptError);
      throw attemptError;
    }
    
    if (!attemptData) {
      throw new Error(`Tentative avec ID ${attemptId} non trouvée`);
    }
    
    // Récupérer toutes les réponses de l'étudiant pour cette tentative
    const { data: studentAnswers, error: answersError } = await supabase
      .from('elearn_quiz_student_answers')
      .select('*')
      .eq('attempt_id', attemptId)
      .eq('tenant_id', tenantId);
    
    if (answersError) {
      console.error('Erreur lors de la récupération des réponses:', answersError);
      throw answersError;
    }
    
    // Récupérer toutes les questions du quiz
    const { data: questions, error: questionsError } = await supabase
      .from('elearn_quiz_questions')
      .select('id, points')
      .eq('quiz_id', attemptData.quiz_id)
      .eq('tenant_id', tenantId);
    
    if (questionsError) {
      console.error('Erreur lors de la récupération des questions:', questionsError);
      throw questionsError;
    }
    
    // Calculer le score réel
    let score = 0;
    let maxScore = 0;
    
    // Calculer le score maximum possible (somme des points de toutes les questions)
    if (questions && questions.length > 0) {
      maxScore = questions.reduce((total, q) => total + (q.points || 0), 0);
    }
    
    // Calculer le score obtenu (somme des points gagnés pour chaque réponse correcte)
    if (studentAnswers && studentAnswers.length > 0) {
      score = studentAnswers.reduce((total, a) => total + (a.points_earned || 0), 0);
    }
    
    // Calculer le pourcentage et déterminer si l'étudiant a réussi
    const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
    
    // Récupérer le score de passage du quiz
    const { data: quizData, error: quizError } = await supabase
      .from('elearn_quizzes')
      .select('passing_score')
      .eq('id', attemptData.quiz_id)
      .eq('tenant_id', tenantId)
      .single();
    
    let passingScore = 60; // Valeur par défaut si non spécifiée
    if (!quizError && quizData) {
      passingScore = quizData.passing_score || 60;
    }
    
    const passed = percentage >= passingScore;
    
    // Mise à jour de la tentative avec le score calculé
    const { data: updatedAttempt, error: updateError } = await supabase
      .from('elearn_quiz_attempts')
      .update({
        status: 'completed',
        score: score,
        max_score: maxScore,
        percentage: percentage,
        passed: passed,
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        time_spent: attemptData.time_spent || 0 // Utiliser la valeur existante ou 0
      })
      .eq('id', attemptId)
      .eq('tenant_id', tenantId)
      .select()
      .single();
    
    if (updateError) {
      console.error('Erreur lors de la mise à jour de la tentative:', updateError);
      throw updateError;
    }
    
    console.log('Tentative mise à jour avec succès:', updatedAttempt);
    return updatedAttempt;
  } catch (error) {
    console.error('Erreur lors du calcul du score du quiz:', error);
    throw error;
  }
};

export const getQuizAttemptHistory = async (quizId: string, studentId: string, tenantId: number): Promise<QuizAttempt[]> => {
  const { data, error } = await supabase
    .from('elearn_quiz_attempts')
    .select('*')
    .eq('quiz_id', quizId)
    .eq('student_id', studentId)
    .eq('tenant_id', tenantId)
    .order('attempt_number', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const getQuizAttemptDetails = async (attemptId: string, tenantId: number): Promise<QuizAttempt & { student_answers: StudentAnswer[] }> => {
  const { data: attempt, error: attemptError } = await supabase
    .from('elearn_quiz_attempts')
    .select('*')
    .eq('id', attemptId)
    .eq('tenant_id', tenantId)
    .single();

  if (attemptError) throw attemptError;

  const { data: studentAnswers, error: answersError } = await supabase
    .from('elearn_quiz_student_answers')
    .select('*')
    .eq('attempt_id', attemptId)
    .eq('tenant_id', tenantId);

  if (answersError) throw answersError;

  return {
    ...attempt,
    student_answers: studentAnswers || []
  };
};

// ==================== STATISTIQUES ET ANALYTICS ====================

export const getQuizStatistics = async (quizId: string, tenantId: number) => {
  const { data, error } = await supabase
    .from('elearn_quiz_attempts')
    .select(`
      id,
      score,
      max_score,
      percentage,
      passed,
      time_spent,
      attempt_number,
      completed_at
    `)
    .eq('quiz_id', quizId)
    .eq('tenant_id', tenantId)
    .eq('status', 'completed');

  if (error) throw error;

  const attempts = data || [];
  const totalAttempts = attempts.length;
  const passedAttempts = attempts.filter(a => a.passed).length;
  const averageScore = totalAttempts > 0 
    ? attempts.reduce((sum, a) => sum + a.percentage, 0) / totalAttempts 
    : 0;
  const averageTime = totalAttempts > 0 
    ? attempts.reduce((sum, a) => sum + a.time_spent, 0) / totalAttempts 
    : 0;

  return {
    totalAttempts,
    passedAttempts,
    passRate: totalAttempts > 0 ? (passedAttempts / totalAttempts) * 100 : 0,
    averageScore: Math.round(averageScore * 100) / 100,
    averageTime: Math.round(averageTime),
    attempts
  };
};

export const getQuestionStatistics = async (questionId: string, tenantId: number) => {
  const { data, error } = await supabase
    .from('elearn_quiz_student_answers')
    .select(`
      is_correct,
      points_earned,
      time_spent,
      elearn_quiz_attempts!inner(status)
    `)
    .eq('question_id', questionId)
    .eq('tenant_id', tenantId)
    .eq('elearn_quiz_attempts.status', 'completed');

  if (error) throw error;

  const answers = data || [];
  const totalAnswers = answers.length;
  const correctAnswers = answers.filter(a => a.is_correct).length;
  const averageTime = totalAnswers > 0 
    ? answers.reduce((sum, a) => sum + a.time_spent, 0) / totalAnswers 
    : 0;

  return {
    totalAnswers,
    correctAnswers,
    correctRate: totalAnswers > 0 ? (correctAnswers / totalAnswers) * 100 : 0,
    averageTime: Math.round(averageTime),
    difficulty: totalAnswers > 0 ? 1 - (correctAnswers / totalAnswers) : 0
  };
};

export const getQuizQuestionsStatistics = async (quizId: string, tenantId: number) => {
  try {
    // Récupérer toutes les questions du quiz
    const { data: questions, error: questionsError } = await supabase
      .from('elearn_quiz_questions')
      .select('id, question_text, points, sort_order')
      .eq('quiz_id', quizId)
      .eq('tenant_id', tenantId)
      .order('sort_order', { ascending: true });

    if (questionsError) throw questionsError;

    if (!questions || questions.length === 0) {
      return [];
    }

    // Pour chaque question, récupérer ses statistiques
    const questionsWithStats = await Promise.all(
      questions.map(async (question) => {
        const stats = await getQuestionStatistics(question.id, tenantId);
        return {
          id: question.id,
          question_text: question.question_text,
          points: question.points,
          sort_order: question.sort_order,
          ...stats
        };
      })
    );

    return questionsWithStats;
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques des questions:', error);
    throw error;
  }
};

// ==================== UTILITAIRES ====================

export const randomizeQuizQuestions = (questions: QuizQuestion[]): QuizQuestion[] => {
  const shuffled = [...questions];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const randomizeAnswers = (answers: QuizAnswer[]): QuizAnswer[] => {
  const shuffled = [...answers];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const formatQuizTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
};

export const validateQuizData = (quiz: Partial<Quiz>): string[] => {
  const errors: string[] = [];

  if (!quiz.title?.trim()) {
    errors.push('Le titre du quiz est requis');
  }

  if (!quiz.lesson_id) {
    errors.push('La leçon associée est requise');
  }

  if (quiz.passing_score !== undefined && (quiz.passing_score < 0 || quiz.passing_score > 100)) {
    errors.push('Le score de passage doit être entre 0 et 100');
  }

  if (quiz.time_limit !== undefined && quiz.time_limit < 0) {
    errors.push('La limite de temps ne peut pas être négative');
  }

  if (quiz.max_attempts !== undefined && quiz.max_attempts < 1) {
    errors.push('Le nombre maximum de tentatives doit être au moins 1');
  }

  return errors;
};

export const validateQuestionData = (question: Partial<QuizQuestion>): string[] => {
  const errors: string[] = [];

  if (!question.question_text?.trim()) {
    errors.push('Le texte de la question est requis');
  }

  if (!question.question_type) {
    errors.push('Le type de question est requis');
  }

  if (question.points !== undefined && question.points < 0) {
    errors.push('Les points ne peuvent pas être négatifs');
  }

  return errors;
};
